import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  VStack,
  HStack,
  Text,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Divider,
  Spinner,
  Center,
  useColorModeValue,
  Card,
  CardHeader,
  CardBody,
} from '@chakra-ui/react'
import { AddIcon, EditIcon, DeleteIcon, ViewIcon } from '@chakra-ui/icons'
import axios from 'axios'
import { hasPermission } from '../config/permissions'
import RichTextEditor from '../components/RichTextEditor'

interface Inquiry {
  id: number
  title: string
  content: string
  author: string
  status: '문의' | '진행중' | '보류' | '완료'
  created_at: string
  updated_at: string
  comment_count: number
}

interface Comment {
  id: number
  inquiry_id: number
  content: string
  author: string
  created_at: string
}

const InquiryBoardPage: React.FC = () => {
  const [inquiries, setInquiries] = useState<Inquiry[]>([])
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null)
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(false)
  const [filterStatus, setFilterStatus] = useState<string>('')
  
  // 폼 상태
  const [formData, setFormData] = useState({
    title: '',
    content: ''
  })
  const [commentContent, setCommentContent] = useState('')
  const [editorKey, setEditorKey] = useState(0) // React Quill 강제 리렌더링용
  
  // 모달 제어
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure()
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  
  const toast = useToast()
  const cancelRef = useRef<HTMLButtonElement>(null)
  
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  
  // 현재 사용자 정보
  const username = localStorage.getItem('username') || ''
  const userRole = (localStorage.getItem('userRole') || 'admin') as 'admin' | 'bexco'

  // 수정 모달 닫기 (폼 초기화)
  const handleEditClose = () => {
    setFormData({ title: '', content: '' })
    setSelectedInquiry(null)
    onEditClose()
  }

  // 등록 모달 닫기 (폼 초기화)
  const handleCreateClose = () => {
    setFormData({ title: '', content: '' })
    onCreateClose()
  }

  // 목록 조회
  const fetchInquiries = async () => {
    setLoading(true)
    try {
      const params: any = {}
      if (filterStatus) {
        params.status = filterStatus
      }
      
      const response = await axios.get('/graph/inquiry-board', { params })
      setInquiries(response.data.data.inquiries)
    } catch (error: any) {
      toast({
        title: '목록 조회 실패',
        description: error.response?.data?.message || '목록을 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
      })
    } finally {
      setLoading(false)
    }
  }

  // 상세 조회
  const fetchInquiryDetail = async (id: number) => {
    try {
      const [inquiryRes, commentsRes] = await Promise.all([
        axios.get(`/graph/inquiry-board/${id}`),
        axios.get(`/graph/inquiry-board/${id}/comments`)
      ])
      
      setSelectedInquiry(inquiryRes.data.data)
      setComments(commentsRes.data.data)
      onViewOpen()
    } catch (error: any) {
      toast({
        title: '조회 실패',
        description: error.response?.data?.message || '상세 정보를 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
      })
    }
  }

  // 등록
  const handleCreate = async () => {
    if (!formData.title || !formData.content) {
      toast({
        title: '입력 오류',
        description: '제목과 내용을 모두 입력해주세요.',
        status: 'warning',
        duration: 3000,
      })
      return
    }

    try {
      await axios.post('/graph/inquiry-board', formData)
      toast({
        title: '등록 완료',
        description: '문의가 성공적으로 등록되었습니다.',
        status: 'success',
        duration: 3000,
      })
      fetchInquiries()
      handleCreateClose()
    } catch (error: any) {
      toast({
        title: '등록 실패',
        description: error.response?.data?.message || '등록에 실패했습니다.',
        status: 'error',
        duration: 3000,
      })
    }
  }

  // 수정
  const handleEdit = async () => {
    if (!selectedInquiry) return

    try {
      await axios.put(`/graph/inquiry-board/${selectedInquiry.id}`, formData)
      toast({
        title: '수정 완료',
        description: '게시글이 수정되었습니다.',
        status: 'success',
        duration: 3000,
      })
      fetchInquiries()
      if (isViewOpen) {
        fetchInquiryDetail(selectedInquiry.id)
      }
      handleEditClose()
    } catch (error: any) {
      toast({
        title: '수정 실패',
        description: error.response?.data?.message || '수정에 실패했습니다.',
        status: 'error',
        duration: 3000,
      })
    }
  }

  // 삭제
  const handleDelete = async () => {
    if (!selectedInquiry) return

    try {
      await axios.delete(`/graph/inquiry-board/${selectedInquiry.id}`)
      toast({
        title: '삭제 완료',
        description: '게시글이 삭제되었습니다.',
        status: 'success',
        duration: 3000,
      })
      onDeleteClose()
      onViewClose()
      fetchInquiries()
    } catch (error: any) {
      toast({
        title: '삭제 실패',
        description: error.response?.data?.message || '삭제에 실패했습니다.',
        status: 'error',
        duration: 3000,
      })
    }
  }

  // 상태 변경
  const handleStatusChange = async (id: number, status: string) => {
    try {
      await axios.patch(`/graph/inquiry-board/${id}/status`, { status })
      toast({
        title: '상태 변경 완료',
        description: `상태가 "${status}"(으)로 변경되었습니다.`,
        status: 'success',
        duration: 3000,
      })
      fetchInquiries()
      if (selectedInquiry && selectedInquiry.id === id) {
        fetchInquiryDetail(id)
      }
    } catch (error: any) {
      toast({
        title: '상태 변경 실패',
        description: error.response?.data?.message || '상태 변경에 실패했습니다.',
        status: 'error',
        duration: 3000,
      })
    }
  }

  // 댓글 등록
  const handleAddComment = async () => {
    if (!selectedInquiry || !commentContent.trim()) {
      toast({
        title: '입력 오류',
        description: '댓글 내용을 입력해주세요.',
        status: 'warning',
        duration: 3000,
      })
      return
    }

    try {
      await axios.post(`/graph/inquiry-board/${selectedInquiry.id}/comments`, {
        content: commentContent
      })
      toast({
        title: '댓글 등록 완료',
        status: 'success',
        duration: 2000,
      })
      setCommentContent('')
      fetchInquiryDetail(selectedInquiry.id)
    } catch (error: any) {
      toast({
        title: '댓글 등록 실패',
        description: error.response?.data?.message || '댓글 등록에 실패했습니다.',
        status: 'error',
        duration: 3000,
      })
    }
  }

  // 댓글 삭제
  const handleDeleteComment = async (commentId: number) => {
    if (!confirm('댓글을 삭제하시겠습니까?')) return

    try {
      await axios.delete(`/graph/inquiry-comments/${commentId}`)
      toast({
        title: '댓글 삭제 완료',
        status: 'success',
        duration: 2000,
      })
      if (selectedInquiry) {
        fetchInquiryDetail(selectedInquiry.id)
      }
    } catch (error: any) {
      toast({
        title: '댓글 삭제 실패',
        description: error.response?.data?.message || '댓글 삭제에 실패했습니다.',
        status: 'error',
        duration: 3000,
      })
    }
  }

  // 수정/삭제 가능 여부 확인
  const canEditOrDelete = (inquiry: Inquiry) => {
    if (userRole === 'admin') return true
    if (inquiry.status === '진행중') return false
    return inquiry.author === username
  }

  useEffect(() => {
    fetchInquiries()
  }, [filterStatus])

  // 수정 모달이 열릴 때 폼 데이터 설정
  useEffect(() => {
    if (isEditOpen && selectedInquiry) {
      console.log('[DEBUG] 수정 모달 열림 - 데이터 로드:', selectedInquiry)
      console.log('[DEBUG] content 길이:', selectedInquiry.content?.length || 0)
      setFormData({
        title: selectedInquiry.title || '',
        content: selectedInquiry.content || ''
      })
      setEditorKey(prev => prev + 1)
    }
  }, [isEditOpen, selectedInquiry])

  const getStatusColor = (status: string) => {
    switch (status) {
      case '문의': return 'blue'
      case '진행중': return 'orange'
      case '보류': return 'gray'
      case '완료': return 'green'
      default: return 'gray'
    }
  }

  return (
    <Box p={8}>
      {/* 페이지 헤더 */}
      <Box mb={6}>
        <Heading size="lg" color="blue.700" mb={2}>
          문의 게시판
        </Heading>
        <Text color="gray.600">
          사용자 문의사항과 피드백을 확인하고 답변할 수 있습니다.
        </Text>
      </Box>
      
      <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" shadow="sm">
        <CardHeader>
          <HStack justify="flex-end" spacing={4}>
            <Select
              placeholder="전체 상태"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              maxW="200px"
              size="md"
            >
              <option value="문의">문의</option>
              <option value="진행중">진행중</option>
              <option value="보류">보류</option>
              <option value="완료">완료</option>
            </Select>
            {hasPermission(userRole, 'INQUIRY_BOARD_CREATE') && (
              <Button
                leftIcon={<AddIcon />}
                colorScheme="bexco"
                minW="120px"
                size="md"
                onClick={() => {
                  setFormData({ title: '', content: '' })
                  onCreateOpen()
                }}
              >
                문의 등록
              </Button>
            )}
          </HStack>
        </CardHeader>

        <CardBody pt={0}>
          {loading ? (
            <Center py={10}>
              <Spinner size="xl" color="bexco.500" />
            </Center>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>번호</Th>
                <Th>제목</Th>
                <Th>작성자</Th>
                <Th>상태</Th>
                <Th>댓글</Th>
                <Th>작성일</Th>
                <Th>작업</Th>
              </Tr>
            </Thead>
            <Tbody>
              {inquiries.map((inquiry) => (
                <Tr key={inquiry.id} _hover={{ bg: 'gray.50' }}>
                  <Td>{inquiry.id}</Td>
                  <Td
                    maxW="300px"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    cursor="pointer"
                    onClick={() => fetchInquiryDetail(inquiry.id)}
                    _hover={{ color: 'bexco.500', textDecoration: 'underline' }}
                  >
                    {inquiry.title}
                  </Td>
                  <Td>{inquiry.author}</Td>
                  <Td>
                    {userRole === 'admin' ? (
                      <Select
                        size="sm"
                        value={inquiry.status}
                        onChange={(e) => handleStatusChange(inquiry.id, e.target.value)}
                        variant="filled"
                        borderColor={`${getStatusColor(inquiry.status)}.300`}
                        maxW="120px"
                      >
                        <option value="문의">문의</option>
                        <option value="진행중">진행중</option>
                        <option value="보류">보류</option>
                        <option value="완료">완료</option>
                      </Select>
                    ) : (
                      <Badge colorScheme={getStatusColor(inquiry.status)}>
                        {inquiry.status}
                      </Badge>
                    )}
                  </Td>
                  <Td>{inquiry.comment_count}</Td>
                  <Td>{new Date(inquiry.created_at).toLocaleDateString()}</Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        aria-label="상세보기"
                        icon={<ViewIcon />}
                        size="sm"
                        onClick={() => fetchInquiryDetail(inquiry.id)}
                      />
                      {canEditOrDelete(inquiry) && (
                        <>
                          <IconButton
                            aria-label="수정"
                            icon={<EditIcon />}
                            size="sm"
                            colorScheme="blue"
                            onClick={async () => {
                              try {
                                // 상세 조회로 전체 데이터 가져오기
                                const response = await axios.get(`/graph/inquiry-board/${inquiry.id}`)
                                console.log('[DEBUG] 수정을 위한 상세 조회:', response.data.data)
                                setSelectedInquiry(response.data.data)
                                onEditOpen()
                              } catch (error: any) {
                                toast({
                                  title: '조회 실패',
                                  description: '게시글 정보를 불러오는데 실패했습니다.',
                                  status: 'error',
                                  duration: 3000,
                                })
                              }
                            }}
                          />
                          <IconButton
                            aria-label="삭제"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            onClick={() => {
                              setSelectedInquiry(inquiry)
                              onDeleteOpen()
                            }}
                          />
                        </>
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
              {inquiries.length === 0 && (
                <Tr>
                  <Td colSpan={7} textAlign="center" py={8} color="gray.500">
                    등록된 문의가 없습니다.
                  </Td>
                </Tr>
              )}
            </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* 등록 모달 */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxW="900px">
          <ModalHeader>문의 등록</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>제목</FormLabel>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="제목을 입력하세요"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>내용</FormLabel>
                <RichTextEditor
                  value={formData.content}
                  onChange={(value) => setFormData({ ...formData, content: value })}
                  placeholder="내용을 입력하세요..."
                  height="400px"
                />
                <Text fontSize="xs" color="gray.500" mt={2}>
                  💡 이미지 버튼을 클릭하여 이미지를 업로드할 수 있습니다. (최대 5MB)
                </Text>
              </FormControl>
              <Flex w="full" justify="flex-end" gap={2}>
                <Button onClick={handleCreateClose}>취소</Button>
                <Button colorScheme="bexco" onClick={handleCreate}>등록</Button>
              </Flex>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* 수정 모달 */}
      <Modal isOpen={isEditOpen} onClose={handleEditClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxW="900px">
          <ModalHeader>문의 수정</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {isEditOpen && (
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>제목</FormLabel>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>내용</FormLabel>
                  <RichTextEditor
                    key={`edit-${editorKey}`}
                    value={formData.content}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                    placeholder="내용을 입력하세요..."
                    height="400px"
                  />
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    💡 이미지 버튼을 클릭하여 이미지를 업로드할 수 있습니다. (최대 5MB)
                  </Text>
                </FormControl>
                <Flex w="full" justify="flex-end" gap={2}>
                  <Button onClick={handleEditClose}>취소</Button>
                  <Button colorScheme="blue" onClick={handleEdit}>수정</Button>
                </Flex>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* 상세보기 모달 */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>문의 상세</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedInquiry && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <HStack justify="space-between" mb={2}>
                    <Heading size="md">{selectedInquiry.title}</Heading>
                    <Badge colorScheme={getStatusColor(selectedInquiry.status)} fontSize="md">
                      {selectedInquiry.status}
                    </Badge>
                  </HStack>
                  <HStack fontSize="sm" color="gray.600" spacing={4}>
                    <Text>작성자: {selectedInquiry.author}</Text>
                    <Text>작성일: {new Date(selectedInquiry.created_at).toLocaleString()}</Text>
                  </HStack>
                </Box>

                <Divider />

                <Box
                  p={4}
                  bg="gray.50"
                  borderRadius="md"
                  minH="200px"
                  dangerouslySetInnerHTML={{ __html: selectedInquiry.content }}
                  sx={{
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                      display: 'block',
                      margin: '10px 0',
                      borderRadius: 'md',
                    },
                    '& p': {
                      marginBottom: '10px',
                    },
                  }}
                />

                <Divider />

                {/* 댓글 영역 */}
                <Box>
                  <Heading size="sm" mb={3}>댓글 ({comments.length})</Heading>
                  <VStack align="stretch" spacing={3} mb={4}>
                    {comments.map((comment) => (
                      <Box key={comment.id} p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                        <HStack justify="space-between" mb={2}>
                          <HStack>
                            <Text fontWeight="bold" fontSize="sm">{comment.author}</Text>
                            <Text fontSize="xs" color="gray.500">
                              {new Date(comment.created_at).toLocaleString()}
                            </Text>
                          </HStack>
                          {(comment.author === username || userRole === 'admin') && (
                            <IconButton
                              aria-label="댓글 삭제"
                              icon={<DeleteIcon />}
                              size="xs"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => handleDeleteComment(comment.id)}
                            />
                          )}
                        </HStack>
                        <Text fontSize="sm">{comment.content}</Text>
                      </Box>
                    ))}
                    {comments.length === 0 && (
                      <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                        아직 댓글이 없습니다.
                      </Text>
                    )}
                  </VStack>

                  {/* 댓글 작성 */}
                  <VStack spacing={2}>
                    <Textarea
                      value={commentContent}
                      onChange={(e) => setCommentContent(e.target.value)}
                      placeholder="댓글을 입력하세요"
                      rows={3}
                    />
                    <Flex w="full" justify="flex-end">
                      <Button
                        size="sm"
                        colorScheme="bexco"
                        onClick={handleAddComment}
                        isDisabled={!commentContent.trim()}
                      >
                        댓글 등록
                      </Button>
                    </Flex>
                  </VStack>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* 삭제 확인 다이얼로그 */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>게시글 삭제</AlertDialogHeader>
            <AlertDialogBody>
              정말로 이 게시글을 삭제하시겠습니까?
              <br />
              게시글과 관련된 모든 댓글이 함께 삭제됩니다.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                취소
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                삭제
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}

export default InquiryBoardPage

